import { AnimatePresence, motion } from "framer-motion";
import {
	Book,
	Check,
	Crown,
	Download,
	Moon,
	Send,
	Sticker,
	Sun,
	Twitter,
	X,
	Monitor,
	Database,
	Target,
} from "lucide-react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/hooks/useLanguage";
import { usePWA } from "@/contexts/pwa-context";
import { useView } from "@/contexts/view-context";
import { translations } from "@/lib/translations";
import { ViewSearchModal } from "./ViewSearchModal";
import { TokenBrief } from "@/components/shared/TokenBrief";

interface SettingsPanelProps {
	isOpen: boolean;
	onClose: () => void;
}

const LanguageOption = ({ lang, currentLang, setLang, onClose }: any) => (
	<button
		onClick={() => {
			setLang(lang.code);
			onClose();
		}}
		className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
	>
		<span>{lang.name}</span>
		{currentLang === lang.code && <Check className="w-4 h-4 text-purple-600" />}
	</button>
);

const SocialLink = ({ href, icon: Icon, text }: any) => (
	<a
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
	>
		<Icon className="w-5 h-5" />
		<span className="text-sm">{text}</span>
	</a>
);

export default function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
	const { theme, setTheme } = useTheme();
	const { language, setLanguage } = useLanguage();
	const { viewMode, setViewMode, focusTokenId, setFocusTokenId } = useView();
	const { install, isStandalone } = usePWA();
	const t = translations[language];
	const vt = (translations[language] as any).view;
	const [isSearchOpen, setIsSearchOpen] = useState(false);

	const viewModes = [
		{ id: "standard", name: vt.standard, icon: Monitor, color: "text-blue-500" },
		{ id: "pro", name: vt.pro, icon: Database, color: "text-cyan-500" },
		{ id: "focus", name: vt.focus, icon: Target, color: "text-light-primary" },
	];

	const languages = [
		{ code: "en", name: "English" },
		{ code: "zh", name: "简体中文" },
	];

	const backdropVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
	};

	const panelVariants = {
		hidden: { x: "100%" },
		visible: { x: "0%" },
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<>
					<motion.div
						className="fixed inset-0 bg-black/60 z-[80]"
						variants={backdropVariants}
						initial="hidden"
						animate="visible"
						exit="hidden"
						onClick={onClose}
						transition={{ duration: 0.3 }}
					/>
					<motion.div
						className="fixed top-0 right-0 h-full w-full max-w-xs bg-white dark:bg-gray-800 shadow-lg z-[90] flex flex-col"
						variants={panelVariants}
						initial="hidden"
						animate="visible"
						exit="hidden"
						transition={{ type: "spring", stiffness: 300, damping: 30 }}
					>
						<div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
							<h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
								{t.settings.title}
							</h2>
							<button
								onClick={onClose}
								className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
							>
								<X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
							</button>
						</div>

						<div className="p-4 space-y-6 flex-grow">
							{/* Theme Toggle */}
							<div>
								<h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
									{t.settings.theme}
								</h3>
								<div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
									<div className="flex items-center space-x-2">
										<Sun className="w-5 h-5 text-yellow-500" />
										<Label
											htmlFor="theme-switch"
											className="text-sm text-gray-700 dark:text-gray-300"
										>
											{t.settings.themeToggle}
										</Label>
									</div>
									<Switch
										id="theme-switch"
										checked={theme === "dark"}
										onCheckedChange={(checked) =>
											setTheme(checked ? "dark" : "light")
										}
										aria-label="Toggle theme"
									/>
									<Moon className="w-5 h-5 text-purple-400" />
								</div>
							</div>

							{/* Language Selection */}
							<div>
								<h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
									{t.settings.language}
								</h3>
								<div className="space-y-1">
									{languages.map((lang) => (
										<LanguageOption
											key={lang.code}
											lang={lang}
											currentLang={language}
											setLang={setLanguage}
											onClose={onClose}
										/>
									))}
								</div>
							</div>

							{/* View Mode Selection */}
							<div>
								<h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
									{vt.title}
								</h3>
								<div className="grid grid-cols-3 gap-2">
									{viewModes.map((mode) => {
										const Icon = mode.icon;
										const isActive = viewMode === mode.id;
										return (
											<button
												key={mode.id}
												onClick={() => {
													setViewMode(mode.id as any);
													if (mode.id !== "focus") onClose();
												}}
												className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${
													isActive
														? "bg-light-primary/10 border-light-primary text-light-primary"
														: "bg-gray-50 dark:bg-gray-700/50 border-transparent text-gray-500"
												}`}
											>
												<Icon className={`w-5 h-5 mb-1 ${isActive ? mode.color : ""}`} />
												<span className="text-[10px] font-medium">{mode.name}</span>
											</button>
										);
									})}
								</div>
								{viewMode === "focus" && (
									<div className="mt-2 p-2 bg-light-primary/5 dark:bg-light-primary/10 rounded-lg flex items-center justify-between">
										<div className="flex-1 min-w-0 mr-2">
											<TokenBrief 
												tokenId={focusTokenId} 
												textClassName="text-[10px] text-light-primary" 
											/>
										</div>
										<button
											onClick={() => setIsSearchOpen(true)}
											className="text-[10px] font-bold text-light-primary hover:underline shrink-0"
										>
											{vt.setting}
										</button>
									</div>
								)}
							</div>

							{/* Social & Action Links */}
							<div>
								<h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
									{t.settings.links}
								</h3>
								<div className="space-y-1">
									{!isStandalone && (
										<button
											onClick={() => {
												install();
												onClose();
											}}
											className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors w-full text-left"
										>
											<Download className="w-5 h-5" />
											<span className="text-sm">
												{t.settings.installApp}
											</span>
										</button>
									)}
									<Link
										href="/profile/vip"
										onClick={onClose}
										className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors w-full text-left"
									>
										<Crown className="w-5 h-5" />
										<span className="text-sm">
											{t.settings.vipBenefits}
										</span>
									</Link>
									<SocialLink
										href="https://x.com/tyche_baby"
										icon={Twitter}
										text="X (Twitter)"
									/>
									<SocialLink
										href="https://t.me/tyche_baby"
										icon={Send}
										text="Telegram"
									/>
									<SocialLink
										href="https://meme.odin.baby"
										icon={Sticker}
										text={t.settings.memeLibrary}
									/>
									<SocialLink
										href="https://www.odin.baby/whitepaper"
										icon={Book}
										text={t.settings.whitepaper}
									/>
								</div>
							</div>
						</div>
					</motion.div>
					<ViewSearchModal
						isOpen={isSearchOpen}
						onClose={() => setIsSearchOpen(false)}
						onSelect={(tokenId) => {
							setFocusTokenId(tokenId);
							setViewMode("focus");
							setIsSearchOpen(false);
							onClose();
						}}
					/>
				</>
			)}
		</AnimatePresence>
	);
}
